import { callRpc } from './base';

export interface VerificationQueueRow {
  programId: string;
  programName: string;
  totalFields: number;
  pendingFields: number;
  lowConfidenceFields: number;
  avgConfidence: number;
  lastExtraction: string;
  priorityScore: number;
}

export interface ProgramVerification {
  id: string;
  fieldName: string;
  fieldCategory: string;
  extractedValue: any;
  sourceSnippet: string;
  sourceUrl: string;
  confidenceScore: number;
  verificationStatus: 'pending_review' | 'in_review' | 'approved' | 'needs_correction' | 'rejected' | 'learning_flagged';
  userAction?: 'approve' | 'edit' | 'reject' | 'flag_learning';
  correctedValue?: any;
  correctionNotes?: string;
  verifiedAt?: string;
}

export interface VerificationQueueParams {
  limit?: number;
  offset?: number;
  confidenceThreshold?: number;
  status?: string;
}

// Raw database response types
interface VerificationQueueRowRaw {
  program_id: string;
  program_name: string;
  total_fields: number;
  pending_fields: number;
  low_confidence_fields: number;
  avg_confidence: number;
  last_extraction: string;
  priority_score: number;
}

interface ProgramVerificationRaw {
  id: string;
  field_name: string;
  field_category: string;
  extracted_value: any;
  source_snippet: string;
  source_url: string;
  confidence_score: number;
  verification_status: string;
  user_action?: string;
  corrected_value?: any;
  correction_notes?: string;
  verified_at?: string;
}

export async function fetchVerificationQueue(params: VerificationQueueParams = {}): Promise<VerificationQueueRow[]> {
  const { limit = 50, offset = 0, confidenceThreshold = 70.0, status } = params;
  
  const rpcParams: Record<string, unknown> = {
    p_limit: limit,
    p_offset: offset,
    p_confidence_threshold: confidenceThreshold,
  };

  if (status) {
    rpcParams.p_status = status;
  }

  return callRpc<VerificationQueueRowRaw[], VerificationQueueRow[]>(
    'rpc_get_verification_queue',
    {
      params: rpcParams,
      transform: (rows) => rows.map((row) => ({
        programId: row.program_id,
        programName: row.program_name,
        totalFields: row.total_fields,
        pendingFields: row.pending_fields,
        lowConfidenceFields: row.low_confidence_fields,
        avgConfidence: row.avg_confidence,
        lastExtraction: row.last_extraction,
        priorityScore: row.priority_score,
      })),
    }
  );
}

export async function fetchProgramVerifications(programId: string): Promise<ProgramVerification[]> {
  return callRpc<ProgramVerificationRaw[], ProgramVerification[]>(
    'rpc_get_program_verifications',
    {
      params: { p_program_id: programId },
      transform: (rows) => rows.map((row) => ({
        id: row.id,
        fieldName: row.field_name,
        fieldCategory: row.field_category,
        extractedValue: row.extracted_value,
        sourceSnippet: row.source_snippet,
        sourceUrl: row.source_url,
        confidenceScore: row.confidence_score,
        verificationStatus: row.verification_status as ProgramVerification['verificationStatus'],
        userAction: row.user_action as ProgramVerification['userAction'],
        correctedValue: row.corrected_value,
        correctionNotes: row.correction_notes,
        verifiedAt: row.verified_at,
      })),
    }
  );
}

export async function updateVerification(
  verificationId: string,
  userAction: 'approve' | 'edit' | 'reject' | 'flag_learning',
  correctedValue?: any,
  correctionNotes?: string,
  verifiedBy?: string
): Promise<boolean> {
  const params: Record<string, unknown> = {
    p_verification_id: verificationId,
    p_user_action: userAction,
  };

  if (correctedValue !== undefined) {
    params.p_corrected_value = correctedValue;
  }
  if (correctionNotes) {
    params.p_correction_notes = correctionNotes;
  }
  if (verifiedBy) {
    params.p_verified_by = verifiedBy;
  }

  return callRpc<boolean, boolean>(
    'rpc_update_verification',
    { params }
  );
}

export async function createSampleVerifications(): Promise<string> {
  return callRpc<string, string>(
    'rpc_create_sample_verifications',
    { params: {} }
  );
}

// Utility functions for confidence scoring and field categorization
export const getConfidenceColor = (score: number): string => {
  if (score >= 80) return '#10b981'; // green
  if (score >= 60) return '#f59e0b'; // yellow
  return '#ef4444'; // red
};

export const getConfidenceLabel = (score: number): string => {
  if (score >= 80) return 'High';
  if (score >= 60) return 'Medium';
  return 'Low';
};

export const getPriorityLabel = (score: number): string => {
  if (score >= 100) return 'Critical';
  if (score >= 50) return 'High';
  if (score >= 20) return 'Medium';
  return 'Low';
};

export const formatFieldName = (fieldName: string): string => {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};